// src/features/insight-v2-shared/components/MetricPanelSection.tsx
//
// One `WidgetCard` listing a group of `MetricResultDto` cells — the supporting
// panels of Operación and the lists of Conciliación, Control and Encaje. Each
// cell is shaped by `buildMetricView` (a figure, a labelled list or a table,
// every key named in the reader's language) and laid out here; nothing in this file reads a
// wire key.
import React from 'react';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useLocaleState, useTranslate } from 'react-admin';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable } from '@/components/common/ResponsiveMetricsTable';
import { MetricStamp } from './MetricStamp';
import { buildMetricView, metricLabel, type MetricListItem, type MetricTableRow, type MetricView } from '@/lib/v2MetricView';
import type { MetricResultDto } from '@/types/analyticsV2';

const ListRows: React.FC<{ items: MetricListItem[]; nested?: boolean }> = ({ items, nested }) => (
  <Stack spacing={0.5} sx={nested ? { pl: 2 } : undefined}>
    {items.map((item) =>
      item.children ? (
        <Box key={item.key}>
          <Typography variant="body2" color="text.secondary">
            {item.label}
          </Typography>
          <ListRows items={item.children} nested />
        </Box>
      ) : (
        <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
            {item.label}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0, textAlign: 'right' }}>
            {item.value}
          </Typography>
        </Box>
      )
    )}
  </Stack>
);

const MetricBody: React.FC<{ view: MetricView }> = ({ view }) => {
  if (view.kind === 'scalar') {
    return (
      <Typography variant="body2" fontWeight={700}>
        {view.text}
      </Typography>
    );
  }
  if (view.kind === 'list') return <ListRows items={view.items} />;
  return (
    <ResponsiveMetricsTable<MetricTableRow>
      columns={view.columns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align,
        render: (row) => row.cells[column.key],
      }))}
      rows={view.rows}
      getRowKey={(row) => row.key}
    />
  );
};

const MetricRow: React.FC<{ metric: MetricResultDto }> = ({ metric }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const view = React.useMemo(() => buildMetricView(metric, translate, locale), [metric, translate, locale]);

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="overline" sx={{ display: 'block', mb: 0.25 }}>
        {metricLabel(metric, translate)}
      </Typography>
      <MetricBody view={view} />
      <MetricStamp stamp={metric.stamp} />
    </Box>
  );
};

interface MetricPanelSectionProps {
  title: string;
  /** One line under the title saying where the figures come from, when that qualifies them. */
  description?: string;
  metrics: MetricResultDto[];
  loading?: boolean;
}

export const MetricPanelSection: React.FC<MetricPanelSectionProps> = ({ title, description, metrics, loading }) => {
  if (!loading && metrics.length === 0) return null;

  return (
    <WidgetCard title={title} description={description} sx={{ mb: 2.5 }}>
      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="60%" />
        </Stack>
      ) : (
        metrics.map((metric, index) => (
          <React.Fragment key={metric.id}>
            {index > 0 && <Divider sx={{ opacity: 0.6 }} />}
            <MetricRow metric={metric} />
          </React.Fragment>
        ))
      )}
    </WidgetCard>
  );
};

export default MetricPanelSection;
